/**
 * SupportReplyController — Stimulus controller for the server-rendered conversation show view.
 *
 * Manages the reply / internal note compose form in the conversations#show view.
 * Provides:
 *   - expandReply()        — opens reply form
 *   - expandNote()         — opens note form
 *   - collapseReply()      — hides expanded form
 *   - insertSavedReply(content) — inserts text into the compose textarea
 *   - injectSignature(html)     — appends plain-text signature to textarea
 *   - openSavedReplies()   — trigger saved replies picker (placeholder; Preact handles this)
 */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'trigger',
    'expandedForm',
    'bodyTextarea',
    'modeLabel',
    'modeInput',
    'compose',
    'signatureIndicator',
  ]

  static values = {
    conversationId: Number,
    mailboxId:      Number,
  }

  connect() {
    this._mode = null
  }

  // ── Reply mode ─────────────────────────────────────────────────────────────

  expandReply(e) {
    e?.preventDefault?.()
    this._mode = 'reply'
    this._expand('Válasz')
    if (this.hasModeInputTarget) this.modeInputTarget.value = 'reply'
  }

  expandNote(e) {
    e?.preventDefault?.()
    this._mode = 'note'
    this._expand('Belső megjegyzés')
    if (this.hasModeInputTarget) this.modeInputTarget.value = 'note'
    // Visually distinguish note form
    if (this.hasExpandedFormTarget) {
      this.expandedFormTarget.classList.add('bg-warning-subtle', 'border-warning')
      this.expandedFormTarget.classList.remove('bg-body-tertiary')
    }
    // Hide signature indicator for notes
    if (this.hasSignatureIndicatorTarget) {
      this.signatureIndicatorTarget.classList.add('d-none')
    }
  }

  collapseReply(e) {
    e?.preventDefault?.()
    this._mode = null
    if (this.hasTriggerTarget)      this.triggerTarget.classList.remove('d-none')
    if (this.hasExpandedFormTarget) {
      this.expandedFormTarget.classList.add('d-none')
      this.expandedFormTarget.classList.remove('bg-warning-subtle', 'border-warning')
      this.expandedFormTarget.classList.add('bg-body-tertiary')
    }
    if (this.hasBodyTextareaTarget) this.bodyTextareaTarget.value = ''
  }

  _expand(modeLabel) {
    if (this.hasTriggerTarget)      this.triggerTarget.classList.add('d-none')
    if (this.hasExpandedFormTarget) this.expandedFormTarget.classList.remove('d-none')
    if (this.hasModeLabelTarget)    this.modeLabelTarget.textContent = modeLabel

    requestAnimationFrame(() => this.bodyTextareaTarget?.focus())
  }

  // ── Saved replies ──────────────────────────────────────────────────────────

  openSavedReplies(e) {
    e?.preventDefault?.()
    // When the Preact SupportApp is mounted this is handled by ReplyCompose.
    // In the static server-rendered view, fall back to fetching and showing a basic picker.
    // For the MVP the Preact component handles this; this method is a no-op here.
  }

  /**
   * Inserts saved reply content into the compose textarea.
   * Called programmatically (e.g. from a picker result).
   * @param {string} content — plain text body to insert
   */
  insertSavedReply(content) {
    const textarea = this.hasBodyTextareaTarget ? this.bodyTextareaTarget : null
    if (!textarea) return

    const start = textarea.selectionStart
    const end   = textarea.selectionEnd
    const current = textarea.value
    textarea.value = current.slice(0, start) + content + current.slice(end)
    textarea.setSelectionRange(start + content.length, start + content.length)
    textarea.focus()
  }

  /**
   * Appends plain-text signature to the compose area.
   * @param {string} html — signature HTML; stripped to plain text before appending
   */
  injectSignature(html) {
    const textarea = this.hasBodyTextareaTarget ? this.bodyTextareaTarget : null
    if (!textarea) return

    const plain = (html || '').replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim()
    if (!plain) return

    const existing = textarea.value
    textarea.value = existing + '\n\n' + plain
    textarea.focus()
  }
}
